import React, { useEffect, useState } from 'react';
import { 
  Card, Table, Button, Modal, Form, Input, Select, 
  Switch, message, Space, Tag, Popconfirm, Tooltip, Typography, Avatar, Grid
} from 'antd';
import { 
  PlusOutlined, DeleteOutlined, EditOutlined, 
  ThunderboltOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { autoPostService } from '../../services/autopost.service';
import { pagesService } from '../../services/pages.service';
import { AutoPostRule, Page } from '../../types';
import PageSelect from '../../components/Shared/PageSelect';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const AutoPostList: React.FC = () => {
  const [rules, setRules] = useState<AutoPostRule[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();
  
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesData, pagesData] = await Promise.all([
        autoPostService.getAll(),
        pagesService.getAll(),
      ]);
      setRules(rulesData);
      setPages(pagesData);
    } catch (error) {
      message.error('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      if (editingId) {
        await autoPostService.update(editingId, values);
        message.success('Cập nhật quy tắc thành công');
      } else {
        await autoPostService.create(values);
        message.success('Tạo quy tắc thành công');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    } catch (error: any) {
      if (error.response && error.response.data && error.response.data.message) {
          message.error(error.response.data.message);
      } else {
          message.error('Thao tác thất bại');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await autoPostService.delete(id);
      message.success('Đã xóa quy tắc');
      fetchData();
    } catch (error: any) {
      if (error.response && error.response.data && error.response.data.message) {
          message.error(error.response.data.message);
      } else {
          message.error('Không thể xóa quy tắc');
      }
    }
  };

  const openEditModal = (record: AutoPostRule) => {
    setEditingId(record.id);
    
    const sourcePageId = typeof record.sourcePageId === 'object' 
      ? ((record.sourcePageId as any)._id || (record.sourcePageId as any).id) 
      : record.sourcePageId;
      
    const targetPageIds = Array.isArray(record.targetPageIds) 
      ? record.targetPageIds.map((p: any) => typeof p === 'object' ? (p._id || p.id) : p)
      : [record.targetPageIds];

    form.setFieldsValue({
      sourcePageId,
      targetPageIds,
      isActive: record.isActive,
    });
    setModalVisible(true);
  };

  const getPageName = (id: string | any) => {
    if (!id) return 'Không xác định';
    if (typeof id === 'object' && id.name) return id.name;
    
    const targetId = typeof id === 'object' ? (id._id || id.id || id.pageId) : id;
    const page = pages.find(p => p.id === targetId || p.pageId === targetId);
    return page ? page.name : 'Không xác định';
  };

  const columns = [
    {
      title: 'Luồng bài viết (Nguồn → Đích)',
      key: 'flow',
      width: isMobile ? 250 : 350,
      render: (_: any, record: AutoPostRule) => (
        <Space direction="vertical" size="small">
          <Tag color="blue" icon={<ThunderboltOutlined />} style={{ fontSize: isMobile ? 11 : 12 }}>Nguồn: {getPageName(record.sourcePageId)}</Tag>
          <div style={{ paddingLeft: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Đăng sang:</Text>
            <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {Array.isArray(record.targetPageIds) && record.targetPageIds.map((id: any) => (
                <Tag key={typeof id === 'object' ? (id._id || id.id) : id} style={{ margin: 0, fontSize: 11 }}>{getPageName(id)}</Tag>
              ))}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'isActive',
      width: 150,
      render: (_: any, record: AutoPostRule) => (
        <Space direction={isMobile ? "vertical" : "horizontal"} align={isMobile ? "start" : "center"} size={isMobile ? 4 : 8}>
          {record.isActive ? (
            <Tag color="green" style={{ margin: 0, fontSize: isMobile ? 11 : 12 }}>Đang chạy</Tag>
          ) : (
            <Tag color="red" style={{ margin: 0, fontSize: isMobile ? 11 : 12 }}>Đã tắt</Tag>
          )}
          {!isMobile && <Tag color="cyan" style={{ margin: 0, fontSize: 11 }}>Real-time</Tag>}
        </Space>
      )
    },
    {
      title: 'Lần chạy cuối',
      dataIndex: 'lastExecutedAt',
      key: 'lastExecutedAt',
      width: 150,
      render: (date: string) => <span style={{ fontSize: isMobile ? 12 : 14 }}>{date ? new Date(date).toLocaleString('vi-VN') : 'Chưa có bài mới'}</span>,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: (isMobile ? 'right' : undefined) as any,
      render: (_: any, record: AutoPostRule) => (
        <Space>
          <Button size={isMobile ? 'small' : 'middle'} icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Popconfirm title="Xóa quy tắc này?" onConfirm={() => handleDelete(record.id)}>
            <Button size={isMobile ? 'small' : 'middle'} danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Watch sourcePageId to filter target pages
  const selectedSourceId = Form.useWatch('sourcePageId', form);

  return (
    <Space direction="vertical" size={isMobile ? "middle" : "large"} style={{ width: '100%' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 16
      }}>
        <div>
          <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Quy tắc tự động</Title>
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
            <InfoCircleOutlined /> {isMobile ? "Tự động sao chép bài viết mới." : "Tự động sao chép bài viết mới từ trang nguồn sang các trang đích ngay khi có bài đăng mới."}
          </Text>
        </div>
        <Button 
          type="primary" 
          size={isMobile ? "middle" : "large"} 
          icon={<PlusOutlined />} 
          block={isMobile}
          onClick={() => {
            setEditingId(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          {isMobile ? "Thiết lập quy tắc" : "Thiết lập quy tắc mới"}
        </Button>
      </div>

      <Card bordered={false} bodyStyle={{ padding: isMobile ? '12px' : '24px' }} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Table 
          columns={columns} 
          dataSource={rules} 
          rowKey="id" 
          loading={loading} 
          scroll={{ x: 'max-content' }}
          pagination={isMobile ? { size: 'small' } : undefined}
        />
      </Card>

      <Modal
        title={editingId ? "Chỉnh sửa quy tắc" : "Thiết lập quy tắc đăng bài tự động"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ isActive: true }}>
          <Form.Item name="sourcePageId" label="Trang nguồn (Theo dõi bài viết mới)" rules={[{ required: true, message: 'Vui lòng chọn trang theo dõi' }]}>
            <PageSelect 
              placeholder="Chọn trang nguồn" 
              showSearch 
              pages={pages} 
            />
          </Form.Item>

          <Form.Item name="targetPageIds" label="Các trang đích (Nơi sẽ đăng bài)" rules={[{ required: true, message: 'Vui lòng chọn ít nhất một trang đích' }]}>
            <PageSelect 
              mode="multiple" 
              placeholder="Chọn các trang sẽ nhận bài viết" 
              showSearch 
              pages={pages.filter(p => p.id !== selectedSourceId)} 
            />
          </Form.Item>

          <Form.Item name="isActive" label="Trạng thái quy tắc" valuePropName="checked">
            <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
          </Form.Item>

          <div style={{ marginBottom: 24, padding: '12px 16px', background: '#e6f7ff', borderRadius: 8, border: '1px solid #91d5ff' }}>
            <Text style={{ fontSize: isMobile ? 12 : 13 }}><ThunderboltOutlined style={{ color: '#1890ff' }} /> <b>Cơ chế:</b> Hệ thống sử dụng Webhook để phát hiện bài viết mới tức thì.</Text>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexDirection: isMobile ? 'column-reverse' : 'row' }}>
              <Button onClick={() => setModalVisible(false)} block={isMobile}>Hủy</Button>
              <Button type="primary" htmlType="submit" loading={submitting} block={isMobile}>
                {editingId ? "Lưu thay đổi" : "Kích hoạt quy tắc"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default AutoPostList;
